import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../store';

export interface Job {
	id: number;
	title: string;
	description: string;
	location: string;
	job_type: string;
	required_experience: string;
	salary: string;
	requirements: string;
	responsibilities: string;
	benefits: string;
	created_at: string;
	updated_at: string;
}

export interface JobApplication {
	job_posting_id: number;
	name: string;
	email: string;
	phone: string;
	designation: string;
	resume: string;
}

interface JobsState {
	jobs: Job[];
	selectedJob: Job | null;
	loading: 'idle' | 'pending' | 'succeeded' | 'failed';
	applyLoading: 'idle' | 'pending' | 'succeeded' | 'failed';
	error: string | null;
	applyError: string | null;
}

const initialState: JobsState = {
	jobs: [],
	selectedJob: null,
	loading: 'idle',
	applyLoading: 'idle',
	error: null,
	applyError: null,
};

// Async thunk for fetching all jobs
export const fetchJobs = createAsyncThunk<Job[], void, { rejectValue: string }>(
	'jobs/fetchJobs',
	async (_, { rejectWithValue }) => {
		try {
			const backendUrl = process.env.REACT_APP_BACKEND_URL;
			const response = await axios.get(`${backendUrl}/jobs`);
			return response.data.data;
		} catch (err: any) {
			if (axios.isAxiosError(err) && err.response) {
				return rejectWithValue(err.response.data.message || 'Failed to fetch jobs.');
			}
			return rejectWithValue('An unknown error occurred.');
		}
	}
);

// Async thunk for fetching specific job by ID
export const fetchJobById = createAsyncThunk<Job, number, { rejectValue: string }>(
	'jobs/fetchJobById',
	async (jobId, { rejectWithValue }) => {
		try {
			const backendUrl = process.env.REACT_APP_BACKEND_URL;
			const response = await axios.get(`${backendUrl}/jobs/${jobId}`);
			return response.data.data;
		} catch (err: any) {
			if (axios.isAxiosError(err) && err.response) {
				return rejectWithValue(err.response.data.message || 'Failed to fetch job details.');
			}
			return rejectWithValue('An unknown error occurred.');
		}
	}
);

// Async thunk for applying to a job
export const applyForJob = createAsyncThunk<any, JobApplication, { rejectValue: string }>(
	'jobs/applyForJob',
	async (application, { rejectWithValue }) => {
		try {
			const backendUrl = process.env.REACT_APP_BACKEND_URL;
			const response = await axios.post(`${backendUrl}/jobs/apply`, application);
			return response.data;
		} catch (err: any) {
			if (axios.isAxiosError(err) && err.response) {
				return rejectWithValue(err.response.data.message || 'Failed to submit application.');
			}
			return rejectWithValue('An unknown error occurred.');
		}
	}
);

const jobsSlice = createSlice({
	name: 'jobs',
	initialState,
	reducers: {
		setSelectedJob: (state, action) => {
			state.selectedJob = action.payload;
		},
		clearJobsError: (state) => {
			state.error = null;
		},
		clearApplyError: (state) => {
			state.applyError = null;
		},
		resetApplyStatus: (state) => {
			state.applyLoading = 'idle';
			state.applyError = null;
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch jobs
			.addCase(fetchJobs.pending, (state) => {
				state.loading = 'pending';
				state.error = null;
			})
			.addCase(fetchJobs.fulfilled, (state, action) => {
				state.loading = 'succeeded';
				state.jobs = action.payload;
				// Set first job as selected if no job is currently selected
				if (!state.selectedJob && action.payload.length > 0) {
					state.selectedJob = action.payload[0];
				}
			})
			.addCase(fetchJobs.rejected, (state, action) => {
				state.loading = 'failed';
				state.error = action.payload ?? 'Failed to fetch jobs';
			})
			// Fetch job by ID
			.addCase(fetchJobById.pending, (state) => {
				state.loading = 'pending';
				state.error = null;
			})
			.addCase(fetchJobById.fulfilled, (state, action) => {
				state.loading = 'succeeded';
				state.selectedJob = action.payload;
			})
			.addCase(fetchJobById.rejected, (state, action) => {
				state.loading = 'failed';
				state.error = action.payload ?? 'Failed to fetch job details';
			})
			// Apply for job
			.addCase(applyForJob.pending, (state) => {
				state.applyLoading = 'pending';
				state.applyError = null;
			})
			.addCase(applyForJob.fulfilled, (state) => {
				state.applyLoading = 'succeeded';
			})
			.addCase(applyForJob.rejected, (state, action) => {
				state.applyLoading = 'failed';
				state.applyError = action.payload ?? 'Failed to submit application';
			});
	},
});

export const { setSelectedJob, clearJobsError, clearApplyError, resetApplyStatus } = jobsSlice.actions;

export const selectJobs = (state: RootState) => state.jobs;

export default jobsSlice.reducer; 